import {useAuth} from "context/AuthContext";
import {useNotifications, useReadNotification} from "hooks/useNotifications";
import moment from "moment";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";
import {ButtonPrimary} from "ui/base/Button/ButtonStyled";
import {Modal} from "ui/base/modal/Modal";
import {ReportIssueChat} from "ui/blocks/chat/ReportIssueChat";
import {LogoIcon} from "ui/icons";
import {fonts, mediaSizes, themeColor} from "ui/theme";
import {instructorRoute} from "../instructor.routes";
import {NotificationItem} from "./components/NotificationItem";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {clearNotifications} from "api/notifications.api";
import {useToast} from "react-toastify";
import {NoContent} from "ui/base/NoContent/NoContent";
import { LoaderComponent } from "ui/base/loading/LoaderComponent";
import NoMessagesFound from "ui/icons/not-found-icons/noMessages.svg";
import {scrollbarStyles} from "ui/base/scrollbarStyle/scrollbarStyle";

export const Notifications = () => {
    const {userRole} = useAuth();

    const {data, isError, isLoading, fetch} = useNotifications(userRole);
    const {mutate: updateNotification} = useReadNotification(userRole);
    const navigate = useNavigate();
    const queryClient = useQueryClient()

    const [isChatModalVisible, setChatModalVisibility] = useState(false);
    const [isInfoModalVisible, setInfoModalVisibility] = useState(false);
    const [selectedChat, setSelectedChat] = useState(undefined);
    const [selectedNotification, setSelectedNotification] = useState(undefined);

    const {mutate, isLoading: ClearLoading} = useMutation({
        mutationFn: clearNotifications,
        onError: (err) => {
            useToast(
                "error",
                "Error while clearing notifications."
            )
        },
        onSuccess: () => {
            // 1: for teacher notification. 2: for user notificaton
            queryClient.invalidateQueries({queryKey: ["getNotifications", 1]})
        },
    })

    const hasData = !!data;
    const showLoading = isLoading && !isError;
    const showError = !isLoading && isError;

    useEffect(() => {
        const intervalId = setInterval(() => fetch(), 10000);

        return () => clearInterval(intervalId);
    }, [fetch]);

    const showInfoPopup = (item) => {
        setSelectedNotification(item);
        setInfoModalVisibility(true);
    };

    const handleCardPress = (item) => {
        switch (item?.class_notification_type) {
            case "admin_message":
                setSelectedChat(item);
                setChatModalVisibility(true);
                break;

            case "class_request_accepted":
                navigate(instructorRoute.homeInstructor, {
                    state: "class_request_accepted",
                });
                break;
            case "a_new_class_request":
                navigate(instructorRoute.homeInstructor, {
                    state: "class_request_accepted",
                });
                break;
            case "booking_request_cancelled":
                showInfoPopup(item);

                break;
            case "booking_request_rejected":
                showInfoPopup(item);

                break;
            case "cancellation_of_booking_by_instructor":
                showInfoPopup(item);

                break;
            case "cancellation_of_booking_by_student":
                showInfoPopup(item);

                break;

            case "reschedule_request_accepted":
                navigate(instructorRoute.homeInstructor, {
                    state: "reschedule_request_accepted",
                });
                break;
            case "reschedulebooking_request":
                navigate(instructorRoute.homeInstructor, {
                    state: "reschedulebooking_request",
                });
                break;

            case "reschedule_request_rejected":
                navigate(instructorRoute.homeInstructor, {
                    state: "reschedule_request_rejected",
                });
                break;
            case "reschedule_request_rejected":
                navigate(instructorRoute.homeInstructor, {
                    state: "reschedule_request_rejected",
                });
                break;
            case "class_request_rejected":
                navigate(instructorRoute.homeInstructor, {
                    state: "class_request_rejected",
                });
                break;
            default:
                break;
        }

        updateNotification(item?.id?.toString());
    };

    const handleClearAllReadNotifications = () => {
        mutate(1);
    };

    const sections = useMemo(() => ([
        {title: "Unread", key: "unread", data: data?.[0]?.unread ?? []},
        {title: "Read", key: "read", data: data?.[1]?.read ?? [], clearable: true},
    ]), [data]);

    return (
        <Container>
            {hasData && !showError && (data?.[0].unread.length !== 0 || data?.[1].read.length !== 0) && (
                <Sidebar>
                    <Title>
                        Notifications
                    </Title>
                </Sidebar>
            )}

            {showLoading && <LoaderComponent minHeight="120px" />}
            {showError && <p>Error while fetching Notifications.</p>}

            {hasData && !showError && (
                <Content>
                    {sections.map((section, idx) => (
                        section.data.length > 0 && (
                            <div key={idx}>
                                <NotifSection>
                                    <SubTitle>{section.title}</SubTitle>
                                    {section.clearable && (
                                        <ClearButton onClick={handleClearAllReadNotifications}>
                                            {ClearLoading ? "Clearing..." : "Clear all"}
                                        </ClearButton>
                                    )}
                                </NotifSection>

                                <NotificationContainer>
                                    {section.data.map((item, index) => (
                                        <NotificationItem
                                            key={`${section.title}_${index}`}
                                            item={item}
                                            currentRole={userRole}
                                            onPress={handleCardPress}
                                        />
                                    ))}
                                </NotificationContainer>
                            </div>
                        )
                    ))}

                    {sections.every(section => section.data.length === 0) && (
                        <NoContent
                            title="You don't have any notifications."
                            image={NoMessagesFound}
                            size={280}
                        />
                    )}
                </Content>
            )}

            <Modal
                show={isChatModalVisible}
                handleClose={() => {
                    setChatModalVisibility(false);
                    setSelectedChat(undefined);
                }}
                maxWidth={900}
            >
                <Header>
                    <Logo>
                        <LogoIcon/>
                    </Logo>
                </Header>
                <ReportIssueChat item={selectedChat}/>
            </Modal>

            <Modal
                show={isInfoModalVisible}
                handleClose={() => {
                    setInfoModalVisibility(false);
                    setSelectedNotification(undefined);
                }}
                maxWidth={550}
                minHeight={200}
            >
                <ModalInfoContainer>
                    <BoldTitle marginBottom={1.5}>
                        {selectedNotification?.message}
                    </BoldTitle>
                    <BoldTitle marginBottom={1}>
                        {`Class : ${selectedNotification?.json_data?.class_name}`}
                    </BoldTitle>
                    <DateText>
                        {moment(selectedNotification?.updated_at).format(
                            "MMM DD, YYYY - h:mm A"
                        )}
                    </DateText>
                    {
                        selectedNotification?.json_data?.reason && <>
                            <BoldTitle>{"Reason"}</BoldTitle>
                            <ReasonText>{selectedNotification?.json_data?.reason}</ReasonText>
                        </>
                    }
                    <Spacer/>
                    <ButtonPrimary
                        variant={"primary_fade"}
                        style={{
                            width: "80%",
                            fontSize: "12px",
                            padding: "10px 17px",
                        }}
                        onClick={() => {
                            setInfoModalVisibility(false);
                        }}
                    >
                        {"OK"}
                    </ButtonPrimary>
                </ModalInfoContainer>
            </Modal>
        </Container>
    );
};

const Container = styled.div`
    max-width: 1200px;
    margin-inline: auto;
    padding-block: 2rem;
    padding-inline: 1rem;
    display: flex;
    flex-direction: column;
    gap: 3rem;
    font-family: var(--font-secondary);

    min-height: var(--page-height);

    @media (width >= ${mediaSizes.md}) {
        flex-direction: row;
        gap: 2rem;
        height: var(--page-height);
        min-height: auto;
        padding-block: 2.5rem;
        padding-inline: 2rem;
    }
    @media (width >= ${mediaSizes.lg}) {
        height: var(--page-height);
        gap: 4rem;
    }
`;

const ModalInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
    justify-content: space-between;
    align-items: center;

    ${scrollbarStyles}
`;

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 200px;
`;

const Title = styled.h1`
    font-size: ${fonts.sizes.big};
    font-weight: 600;

    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.h4};
    }
    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes["h3.4"]};
    }
`;

const SubTitle = styled.h5`
    padding-bottom: 0.5rem;
    position: sticky;
    top: 0;
    background-color: white;
    @media (width >= ${mediaSizes.md}) {
        padding-bottom: 0rem;
    }
`;

const DateText = styled.p`
    padding-bottom: 0.5rem;
    @media (width >= ${mediaSizes.md}) {
        padding-bottom: 1rem;
    }
`;

const BoldTitle = styled.h3`
    margin-top: 1rem;
    margin-bottom: ${({marginBottom}) => `${marginBottom}rem`};
    font-weight: bold;
    text-align: center;
`;

export const Spacer = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
`;

const ReasonText = styled.p`
    margin-top: 1rem;
    margin-bottom: 2rem;
    max-height: 50%;
    text-align: center;
`;

const NotificationContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    @media (width >= ${mediaSizes.md}) {
        padding-inline: 0.5rem;
    }
    @media (width >= ${mediaSizes.lg}) {
        padding-inline: 1rem;

    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: auto;
    gap: 1rem;
    flex-grow: 1;
    min-height: 0;
    margin-top: 3rem;
    position: relative;
    z-index: 0;
    ${scrollbarStyles}
`;

const Logo = styled.div`
    width: 190px;
    height: 100%;

    svg {
        width: 100%;
        height: 47px;
    }

    a {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    @media (max-width: 1200px) {
        width: 147px;
        svg {
            height: 26px;
        }
    }
    @media (max-width: 640px) {
        width: 99px;
        svg {
            height: 17px;
        }
    }
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    /* background-color: red; */
    align-items: center;
    align-content: center;
    justify-content: center;
`;

const NotifSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    padding-block: 0.5rem;
    background-color: white;
`;

const ClearButton = styled.p`
    margin-right: 2rem;
    background-color: ${themeColor("blue")};
    padding: 8px;
    border-radius: 12px;
    color: ${themeColor("white")};
    cursor: pointer;
    user-select: none;

    font-weight: 500;
    font-size: 0.875rem;
`;
